import {
  MentionComposerInput,
  MentionComposerInputHandle,
  SerializedEditorContent,
} from '@design/chat/mention-composer';
import mentionInputStyles from '@design/chat/mention-composer/mention-composer-input/MentionComposerInput.module.scss';
import { useRows } from '@terminal/components/chat';
import { RenderFeatureFlag } from '@terminal/components/render-feature-flag/RenderFeatureFlag';
import { appPaths } from '@terminal/routes/app-paths';
import { useUserUpload } from '@terminal/views/alfred/research/use-user-upload/useUserUpload';
import { useChatNavigation } from '@terminal/views/alfred/use-chat-navigation/useChatNavigation';
import React, {
  ReactNode,
  Ref,
  RefObject,
  TextareaHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';

import { AlfredTools } from '../alfred-tools/AlfredTools';
import { useMentionSourceBridge } from '../mentions/useMentionSourceBridge';
import { UploadFileStatusDropdown } from '../upload-file-status-dropdown/UploadFileStatusDropdown';
import styles from './ChatDocumentContextInput.module.scss';
import { checkChatInputVisibility } from './utils/checkChatInputVisibility';

export interface ChatDocumentContextInputProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onKeyDown'> {
  children?: ReactNode;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  isMessagePending: boolean;
  onKeyDownProp?: React.KeyboardEventHandler<HTMLTextAreaElement>;
  enableMentions?: boolean;
  mentionInputRef?: Ref<MentionComposerInputHandle>;
  onMentionsChange?: (content: SerializedEditorContent) => void;
  onMentionsSubmit?: () => void;
}

export const ChatDocumentContextInput = ({
  children,
  value,
  isMessagePending,
  inputRef,
  autoFocus,
  placeholder,
  disabled,
  onChange,
  onKeyDownProp,
  enableMentions,
  mentionInputRef,
  onMentionsChange,
  onMentionsSubmit,
}: ChatDocumentContextInputProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const uploadDropdownRef = useRef<HTMLDivElement | null>(null);
  const analysisModeDropdownRef = useRef<HTMLDivElement | null>(null);
  const webSearchDropdownRef = useRef<HTMLDivElement | null>(null);

  const { rows, updateRows } = useRows({
    value: value,
    textAreaRef: inputRef,
  });

  const { mode, previousMode } = useChatNavigation();
  const location = useLocation();
  const isAlfredPage = location.pathname === appPaths.alfred;
  const { files } = useUserUpload(state => ({
    files: state.files,
  }));

  const [showTools, setShowTools] = useState(isAlfredPage);

  const {
    sections,
    loading,
    onQueryChange,
    renderCandidate,
    renderSectionTitle,
    renderPreview,
    unresolvedHintText,
  } = useMentionSourceBridge(!!enableMentions);

  useEffect(() => {
    const handleDocumentClick = (e: globalThis.MouseEvent) => {
      if (isAlfredPage) {
        return;
      }

      const target = e.target;

      if (!(target instanceof HTMLElement)) {
        return;
      }

      const withinTarget = wrapperRef.current?.contains(target);
      const isInsideChatDropdown =
        uploadDropdownRef.current?.contains(target) ||
        analysisModeDropdownRef.current?.contains(target) ||
        webSearchDropdownRef.current?.contains(target) ||
        !!target
          .closest('[data-dropdown-list-container]')
          ?.classList.contains(mentionInputStyles.popupList);

      if (withinTarget || isInsideChatDropdown) {
        setShowTools(true);
        return;
      }

      setShowTools(false);
    };

    document.addEventListener('mousedown', handleDocumentClick);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [isAlfredPage]);

  useEffect(() => {
    if (isAlfredPage || previousMode !== null) {
      setShowTools(true);
    }
  }, [isAlfredPage, previousMode]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateRows(event);
    onChange?.(event);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = e => {
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.stopPropagation();
    } else {
      onKeyDownProp?.(e);
    }
  };

  const {
    hasValidFiles,
    showToolsOnNonAlfredPage,
    showToolsOnAlfredPage,
    showChildrenInTextArea,
    showChildrenInTools,
  } = checkChatInputVisibility({
    files,
    isAlfredPage,
    mode,
    showTools,
  });

  return (
    <div
      className={
        isMessagePending
          ? styles.chatInputFieldWrapperPending
          : styles.chatInputFieldWrapper
      }
    >
      <div className={styles.chatInputFieldContainer} ref={wrapperRef}>
        <RenderFeatureFlag requiredFlag={'alfred_user_upload'}>
          {mode === 'research' && (
            <div
              className={
                hasValidFiles
                  ? styles.uploadingSection
                  : styles.uploadingSectionNoPadding
              }
            >
              <UploadFileStatusDropdown />
            </div>
          )}
        </RenderFeatureFlag>
        <div className={styles.textAreaWrapper} data-chat-textarea-wrapper>
          {enableMentions ? (
            <MentionComposerInput
              ref={mentionInputRef}
              className={styles.textareaElement}
              placeholder={placeholder}
              autoFocus={autoFocus}
              disabled={disabled}
              onChange={onMentionsChange}
              onSubmit={onMentionsSubmit}
              onFocus={() => {
                setShowTools(true);
              }}
              data-testid="chat-input"
              sections={sections}
              loading={loading}
              onQueryChange={onQueryChange}
              renderCandidate={renderCandidate}
              renderSectionTitle={renderSectionTitle}
              renderPreview={renderPreview}
              unresolvedHintText={unresolvedHintText}
            />
          ) : (
            <textarea
              id="dropdown-trigger"
              className={styles.textareaElement}
              ref={inputRef}
              onChange={handleChange}
              rows={rows}
              value={value}
              autoFocus={autoFocus}
              placeholder={placeholder}
              disabled={disabled}
              onFocus={() => {
                setShowTools(true);
              }}
              data-testid="chat-input"
              data-chat-textarea-element
              {...(mode === 'research'
                ? { onKeyDown }
                : { onKeyDown: onKeyDownProp })}
            />
          )}
          {showChildrenInTextArea && children}
        </div>
        {(showToolsOnNonAlfredPage || showToolsOnAlfredPage) && (
          <AlfredTools
            isAlfredPage={isAlfredPage}
            mode={mode}
            isMessagePending={isMessagePending}
            showChildrenInTools={showChildrenInTools}
            uploadDropdownRef={uploadDropdownRef}
            analysisModeDropdownRef={analysisModeDropdownRef}
            webSearchDropdownRef={webSearchDropdownRef}
          >
            {children}
          </AlfredTools>
        )}
      </div>
    </div>
  );
};
